import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import useApi from '../../hooks/useApi';
import { CircularProgress, Box } from '@mui/material';
import { CONTEXT_TYPE_MANAGER, hasMultipleAccountContexts, isSecondaryAdminSession } from '../../utils/accountContext/accountContextUtils';
import { useOrganization } from '../../contexts/OrganizationContext';
import { applyNewUserOnboardingScope } from '../../utils/postLoginLanding';

const cookies = new Cookies();

// ==============================|| ORG LOCATION GUARD ||============================== //

/**
 * Organization and Location guard for routes
 * Redirects to /newLogin if organization count is 0 or all organizations have locationCount === 0
 * Note: locationCount === undefined is treated as valid (org may have locations, count unknown)
 * @param {PropTypes.node} children children element/node
 */
export default function OrgLocationGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateOrganizationContext } = useOrganization();
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const checkOrgLocationCount = async () => {
      // Skip check if already on newLogin page (handle both with and without base path)
      const pathname = location.pathname;
      if (pathname === '/newLogin' || pathname.endsWith('/newLogin')) {
        setIsChecking(false);
        return;
      }

      // Skip check if not authenticated
      // Check for both 'token' and 'access_token' to match axiosInstance.js behavior
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");
      if (!token || token === "null") {
        setIsChecking(false);
        return;
      }

      // Skip redirect for support-agent users - they are intentionally sent to /support/usersList
      // even when their own org list is empty (they manage other users' orgs)
      const supportAccessLevel = ["s3", "s2", "s1"];
      const userAccessLevel = localStorage.getItem("accessLevel");
      const isSupportUser = userAccessLevel && supportAccessLevel.indexOf(userAccessLevel) >= 0;
      // login-as sets userSubRole=supportAgent; accessLevel may still be the customer's.
      const isSupportImpersonation = !!cookies.get('userSubRole');

      // Skip redirect if on support routes, support agent, or impersonating a customer
      if (pathname.startsWith('/support/') || isSupportUser || isSupportImpersonation) {
        setIsChecking(false);
        return;
      }

      try {
        const result = await useApi("GET_ALL_ORG_LIST", {}, {}, []);

        // Only redirect to onboarding for successful responses that explicitly show empty org/location data
        // On errors (status = false) or exceptions, allow navigation to proceed (don't block on API failure)
        if (result && result?.response?.status && result.details) {
          const orgList = Array.isArray(result.details) ? result.details : [];
          
          // Check if organization count is zero
          if (orgList.length === 0) {
            if (isSecondaryAdminSession()) {
              setIsChecking(false);
              return;
            }
            if (hasMultipleAccountContexts() && localStorage.getItem('insightActiveContextType') === CONTEXT_TYPE_MANAGER) {
              setShouldRedirect(true);
              navigate('/account-selection', { replace: true, state: { switchMode: true, accessRevoked: true } });
              return;
            }
            setShouldRedirect(true);
            applyNewUserOnboardingScope(updateOrganizationContext);
            navigate('/newLogin');
            return;
          }

          // Check if all organizations have locationCount === 0
          // Treat locationCount === undefined as valid (org might have locations, we just don't know the count)
          // This matches the behavior in addMultipleDevice.js and other callers
          const hasLocation = orgList.some(org => {
            const locationCount = org.locationCount;
            // Only exclude if explicitly 0; undefined/null or > 0 are both valid
            return locationCount !== 0;
          });

          if (!hasLocation) {
            setShouldRedirect(true);
            applyNewUserOnboardingScope(updateOrganizationContext);
            navigate('/newLogin');
            return;
          }
        }
        // If result exists but status is false (business error) or response structure is unexpected,
        // allow navigation to proceed rather than redirecting to onboarding
      } catch (error) {
        console.error('Error checking organization and location counts:', error);
        // On error, allow navigation to proceed (don't block on API failure)
      } finally {
        setIsChecking(false);
      }
    };
    checkOrgLocationCount();
    // updateOrganizationContext is memoized in OrganizationContext so this effect
    // does not refetch GET_ALL_ORG_LIST on every organization-context render.
  }, [navigate, location.pathname, updateOrganizationContext]);

  // Show loading while checking
  if (isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return children;
}

OrgLocationGuard.propTypes = { children: PropTypes.any };